import { useQuery } from "react-query";
import { sellerApiClient } from "./sellerApiClient";
// import { PaginationModel } from "@hooks/usePagingSearchParams";
// import { FilterModel } from "@hooks/useFiltersSearchParams";

export interface Seller {
  id: string;
  name: string;
  ozonApiExists?: boolean;
  email?: string;
  apiKey?: string;
  apiClientId?: string;
  allowedToDeleteOzon?: boolean;
  /** Можно ли отвязать магазин от аккаунта (для WB/YM — false при активных FBP-связках) */
  allowedToDelete?: boolean;
  ymSeller?: {
    id: string;
    ymCampaignId: string;
    ymBusinessId: string;
    apiKey: string;
  };
  wbSeller?: {
    id: string;
    wbOriginalSellerId: string;
    token: string;
    tokenStatus:
      | "Active"
      | "Expiring"
      | "Expired"
      | "Invalid"
      | "SellerIdMismatch";
    tokenExpiresAt: string;
  };
  aliSeller?: {
    id: string;
    aliOriginalSellerId: string;
    token: string;
    tokenStatus: "Active" | "Invalid";
  };
  [key: string]: any;
}

export interface ICampaigns {
  id: string;
  businessId: string;
  name: string;
  placementType?: string;
}

export interface IWbValidationResponse {
  error: string;
  isSuccess: boolean;
}

export interface IYMValidationResponse {
  error: string;
  isSuccess: boolean;
  campaigns: ICampaigns[];
}

export const validateWbKey = async ({
  token,
  wbSellerId,
}: {
  token: string;
  wbSellerId?: string; // толбко для уже сохраненных магазинов, когда редактируем и обновляем ключ
}): Promise<IWbValidationResponse> => {
  const response = await sellerApiClient.post<IWbValidationResponse>(
    `/v1/sellers/wb/token/validate`,
    { token, wbSellerId },
  );

  return response.data;
};

export const updateWbKey = async ({
  wbSellerId,
  newToken,
}: {
  newToken: string;
  wbSellerId: string;
}): Promise<IWbValidationResponse> => {
  const response = await sellerApiClient.put<IWbValidationResponse>(
    `/v1/sellers/wb/token/update`,
    { newToken, wbSellerId },
  );

  return response.data;
};

export interface IAliValidationResponse {
  error: string;
  isSuccess: boolean;
}

export const validateAliKey = async ({
  token,
  aliSellerId,
}: {
  token: string;
  aliSellerId?: string; // только для уже сохраненных магазинов, когда редактируем и обновляем токен
}): Promise<IAliValidationResponse> => {
  const response = await sellerApiClient.post<IAliValidationResponse>(
    `/v1/sellers/ali/token/validate`,
    { token, aliSellerId },
  );

  return response.data;
};

export const updateAliKey = async ({
  aliSellerId,
  newToken,
}: {
  newToken: string;
  aliSellerId: string;
}): Promise<void> => {
  const response = await sellerApiClient.put<void>(
    `/v1/sellers/ali/token/update`,
    { newToken, aliSellerId },
  );

  return response.data;
};

export const validateYMKey = async (
  apiKey: string,
): Promise<IYMValidationResponse> => {
  const response = await sellerApiClient.post<IYMValidationResponse>(
    `/v1/sellers/ym/api-key/validate`,
    { apiKey },
  );

  return response.data;
};

export const createWbSeller = async ({
  token,
  email,
  ticketId,
  code,
}: {
  token: string;
  email: string;
  ticketId: string | null;
  code: string | null;
}): Promise<void> => {
  const response = await sellerApiClient.post<void>(`/v1/sellers/wb/create`, {
    token,
    email,
    ticketId,
    code,
  });

  return response.data;
};

export const createYMSeller = async (data: {
  apiKey: string;
  ymCampaignId: string;
  email: string;
  ticketId: string | null;
  code: string | null;
  partnerId?: string | null;
}): Promise<void> => {
  const response = await sellerApiClient.post<void>(`/v1/sellers/ym/create`, data);

  return response.data;
};

export interface GetPartnerIdResponse {
  partnerId: string;
}

export const getPartnerId = async (data: {
  campaignId: string;
  apiKey: string;
}): Promise<GetPartnerIdResponse> => {
  const response = await sellerApiClient.post<GetPartnerIdResponse>(
    "/v1/sellers/ym/partner-id/lookup",
    data,
  );
  return response.data;
};

export const createAliSeller = async ({
  token,
  name,
  email,
  ticketId,
  code,
}: {
  token: string;
  name: string;
  email: string;
  ticketId: string | null;
  code: string | null;
}): Promise<void> => {
  const response = await sellerApiClient.post<void>(`/v1/sellers/ali/create`, {
    token,
    name,
    email,
    ticketId,
    code,
  });

  return response.data;
};

export interface GetSellersResponse {
  items: Seller[];
}

export const getSellers = async (): Promise<GetSellersResponse> => {
  const response = await sellerApiClient.get<GetSellersResponse>("/v1/sellers");
  return response.data;
};

export function useSellersFound() {
  const { data, refetch, isFetching, isFetched } = useQuery({
    queryKey: ["sellers"],
    queryFn: () => getSellers(),
    retry: 1,
  });

  return {
    isFetching,
    sellersFound: Boolean(data?.items?.length),
    sellersNotFound: isFetched && !data?.items?.length,
    refetch,
  };
}

export interface CommpanyApiData {
  sellerId: string;
  apiKey?: string;
  apiClientId?: string;
}

export const addOzonApiData = async (data: CommpanyApiData): Promise<void> => {
  const { sellerId, apiKey, apiClientId } = data;
  const response = await sellerApiClient.post(
    `/v1/sellers/${sellerId}/ozon-api-data`,
    {
      apiKey,
      apiClientId,
    },
  );
  return response.data;
};

export const deleteOzonApiData = async (
  data: CommpanyApiData,
): Promise<void> => {
  const { sellerId } = data;
  const response = await sellerApiClient.delete(
    `/v1/sellers/${sellerId}/ozon-api-data`,
  );
  return response.data;
};

export const deleteSeller = async (sellerId: string): Promise<void> => {
  const response = await sellerApiClient.post("/v1/accounts/unlink", {
    sellerId,
  });
  return response.data;
};

export const getMarketplaceId = (seller: Seller) => {
  if (seller.wbSeller) return "Wildberries";
  if (seller.ymSeller) return "Yandex";
  if (seller.aliSeller) return "Aliexpress";
  return "Ozon";
};
